import { defineStore } from 'pinia'
import { RouteRecordName } from 'vue-router'
import { useNuxtApp, useRouter } from '#app'
import { StoreId } from '~/store'
import { UserState } from '~/store/user/user.types'
import { ChangeUserInfoDTO, Code, DeliveryType } from '~/api/modules/user/types'
import { Success, UnauthorizedUserError } from '~/api/results'
import { CallbackProps } from '@/services/vkAuth/vkAuth.types'

const defaultState: UserState = {
  statistics: null,
  info: null,
  code: {
    code_length: 0,
    deliveredBy: DeliveryType.SMS,
    waitSeconds: 0,
  },
  isAuthenticated: false,
  isLoading: true,
  isLoadingStatistic: false,
  isPendingStatistic: false,
}

export const useUserStore = defineStore({
  id: StoreId.USER,
  state: (): UserState => defaultState,
  actions: {
    async getInfo({
      isUserPages,
      urlPage,
    }: {
      serverSide?: boolean
      isUserPages?: boolean
      urlPage?: string
    }) {
      const { $apiService } = useNuxtApp()
      const { push } = useRouter()
      /* в зависимости от того, где происходит запрос(сервер или клиент) устанавливаем
        кастомный заголовок User-Agent-а
      */

      const result = await $apiService.user.getInfo({})

      if (result instanceof Success) {
        this.info = result.data || null

        this.isAuthenticated = true
      } else if (result instanceof UnauthorizedUserError) {
        if (isUserPages) {
          await push('/login/?backUrl=' + urlPage)
        }

        this.isAuthenticated = false
      }

      this.isLoading = false

      return result
    },
    async getStatistic() {
      // Если уже идёт загрузка статистики или данные уже получены, выходим из функции. Обработка на случай пересечения запросов
      if (this.isLoadingStatistic || this.statistics) return

      this.isLoadingStatistic = true

      const { $apiService } = useNuxtApp()

      const result = await $apiService.user.getStatistics()

      if (result instanceof Success) {
        this.statistics = result.data || this.statistics
      } else if (result instanceof UnauthorizedUserError) {
        this.isAuthenticated = false
      }

      this.isLoadingStatistic = false

      return result
    },
    async signIn(bodyRequest: { phone: string; password: string }) {
      const { $apiService } = useNuxtApp()

      const result = await $apiService.user.signIn(bodyRequest)

      if (result instanceof Success && result.data) {
        this.isAuthenticated = true
        this.info = result.data

        window.carrotquest?.auth(result.data.clientId, result.data.clientHash)
        window.carrotquest?.track('$authorized', {
          $phone: result.data.mobile,
          $email: result.data.email,
          $name: result.data.fullName,
        })
      }

      return result
    },
    async changeInfo(bodyRequest: ChangeUserInfoDTO) {
      const { $apiService } = useNuxtApp()
      const result = await $apiService.user.changeInfo(bodyRequest)

      await this.getInfo({
        serverSide: false,
        isUserPages: false,
        urlPage: '',
      })

      return result
    },
    async requestCode(bodyRequest: {
      phone: string
      ui_element: string | RouteRecordName
    }) {
      const { $apiService } = useNuxtApp()
      const result = await $apiService.user.requestCode(bodyRequest)

      if (result instanceof Success) this.code = result.data || this.code

      // Все ошибки возвращаем в компонент для дальнейшей обработки
      return result
    },
    async confirmCode(bodyRequest: {
      code: string
      phone: string
      ui_element: string | RouteRecordName
    }) {
      const { $apiService } = useNuxtApp()
      const result = await $apiService.user.confirmCode(bodyRequest)

      if (result instanceof Success && result.data) {
        this.isAuthenticated = true
        this.info = result.data

        window.carrotquest?.auth(result.data.clientId, result.data.clientHash)
        window.carrotquest?.track('$authorized', {
          $phone: result.data.mobile,
          $email: result.data.email,
          $name: result.data.fullName,
        })

        return result
      }

      return result
    },
    setCode(code: Code | undefined) {
      this.code = code || this.code
    },
    async authByVk({ uuid, token, first_name }: CallbackProps) {
      const { $apiService } = useNuxtApp()

      return await $apiService.user.vkAuth(uuid, token, first_name)
    },
    async signOut() {
      const { $apiService } = useNuxtApp()

      const result = await $apiService.user.signOut()

      if (result instanceof Success) this.$state = defaultState

      return result
    },
  },
  getters: {
    allRefferalPromocodes: (state) =>
      state.info?.referralPromocodes.filter(({ enabled }) => enabled),
    phoneUserFormated: (state) => state.info?.mobile_formatted,
  },
})
